import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Filtros e página de uma listagem, guardados na URL.
 *
 * A página sozinha não resolveria: "página 5" só significa alguma coisa junto
 * do filtro que define a lista. Guardar metade do estado na URL e a outra
 * metade em `useState` faz voltar para a tela devolver a página 5 de uma lista
 * *diferente* - sem a linha que a pessoa tinha aberto. Ou vai tudo, ou não vai
 * nada.
 *
 * De brinde, a tela vira endereço: dá para recarregar, favoritar e mandar o
 * link do que se está vendo para outra pessoa.
 */
export type ValorDeFiltro = string | string[] | number | undefined;

/** Ausente é o padrão: valor vazio sai da URL em vez de virar `?x=`. */
export function escrever(
  params: URLSearchParams,
  chave: string,
  valor: ValorDeFiltro,
): URLSearchParams {
  const texto = Array.isArray(valor) ? valor.join(',') : valor?.toString();
  if (!texto) params.delete(chave);
  else params.set(chave, texto);
  return params;
}

export function lerLista(params: URLSearchParams, chave: string): string[] {
  const bruto = params.get(chave);
  return bruto ? bruto.split(',').filter(Boolean) : [];
}

/** A URL é editável à mão: página inválida cai na primeira, não quebra a tela. */
export function lerPagina(params: URLSearchParams): number {
  const numero = Number(params.get('page'));
  return Number.isInteger(numero) && numero > 0 ? numero : 1;
}

/**
 * Aplica filtros e volta para a primeira página - a página 7 do filtro anterior
 * quase nunca existe no filtro novo.
 */
export function aplicarFiltros(
  atuais: URLSearchParams,
  mudancas: Record<string, ValorDeFiltro>,
): URLSearchParams {
  const proximos = new URLSearchParams(atuais);
  for (const [chave, valor] of Object.entries(mudancas)) escrever(proximos, chave, valor);
  proximos.delete('page');
  return proximos;
}

export function aplicarPagina(
  atuais: URLSearchParams,
  pagina: number,
  /**
   * Filtros que mudam **junto** com a página, na mesma escrita.
   *
   * **Existe porque duas escritas seguidas se atropelam.** Chamar `filtrar` e
   * `irParaPagina` no mesmo gesto parece funcionar e não funciona: as duas
   * recebem os parâmetros de antes, e a segunda desfaz a primeira. Apareceu na
   * Legislação Comentada, onde procurar um artigo tem de **sair** do recorte
   * ativo - a página vem contada sobre a lei inteira, e com o filtro ligado ela
   * apontava para outro lugar da lista.
   */
  mudancas: Record<string, ValorDeFiltro> = {},
): URLSearchParams {
  const proximos = new URLSearchParams(atuais);
  for (const [chave, valor] of Object.entries(mudancas)) escrever(proximos, chave, valor);
  // A primeira página é o padrão, e padrão não ocupa espaço na barra de endereço.
  return escrever(proximos, 'page', pagina > 1 ? pagina : undefined);
}

export function useEstadoDeLista() {
  const [params, setParams] = useSearchParams();

  const ler = useMemo(
    () => ({
      texto: (chave: string): string | undefined => params.get(chave) ?? undefined,
      lista: (chave: string): string[] => lerLista(params, chave),
      pagina: (): number => lerPagina(params),
    }),
    [params],
  );

  /*
   * Filtrar entra como `replace` porque um `Select` múltiplo dispara uma
   * alteração por item escolhido: empilhar histórico a cada etiqueta
   * transformaria o botão voltar do navegador em desfazer item por item.
   */
  const filtrar = useCallback(
    (mudancas: Record<string, ValorDeFiltro>) => {
      setParams((atuais) => aplicarFiltros(atuais, mudancas), { replace: true });
    },
    [setParams],
  );

  /* Já trocar de página é navegação de verdade: voltar devolve a página anterior. */
  const irParaPagina = useCallback(
    (pagina: number, mudancas: Record<string, ValorDeFiltro> = {}) => {
      setParams((atuais) => aplicarPagina(atuais, pagina, mudancas));
    },
    [setParams],
  );

  /** Para quem sai da lista e precisa saber como voltar exatamente para cá. */
  const busca = params.toString();

  return { ler, filtrar, irParaPagina, busca };
}
